/**
 * TxnRow.jsx — Phase 3
 *
 * Single transaction row for the redesigned /transactions list. Feels
 * like a natural sibling of /recurring — same rhythm and chip patterns.
 *
 * Row anatomy (left → right):
 *   • bulk-select checkbox (only when selection mode is active OR row hovered)
 *   • avatar-style account badge (deterministic tint via account id)
 *   • main cluster: description (line 1) + category chip + mode/provenance chips (line 2)
 *   • amount right-aligned with tabular-nums
 *
 * The row is a native <button> (role=button on a div would need explicit
 * key handlers). Row click opens the detail drawer via `onOpen(txn)`.
 * Checkbox click toggles selection instead of opening the drawer — the
 * caller wires that via `onToggleSelect(id)` and stops propagation.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { FileText, Pencil, Camera, Repeat, AlertTriangle, StickyNote } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { prettifyDescription, modeLabel } from '@/lib/txDescription';
import { accountTint, accountInitials, provenanceOf, confidenceOf } from './txnHelpers';

const PROV_ICONS = {
  statement: FileText,
  receipt:   Camera,
  recurring: Repeat,
  manual:    Pencil,
};

export default function TxnRow({
  txn, accountName, currency = 'INR',
  selected = false,
  selectionMode = false,
  onToggleSelect,
  onOpen,
  // Phase 84.20.5 — When search is active and the row matched only via
  // the note field (not description / category / account), we render a
  // small "note" chip so users understand why this row surfaced.
  matchedByNote = false,
}) {
  const pretty = prettifyDescription(txn.description);
  const titleText = pretty.display || txn.category || 'Transaction';
  const tint = accountTint(txn.account_id);
  const initials = accountInitials(accountName);
  const prov = provenanceOf(txn);
  const conf = confidenceOf(txn);
  const ProvIcon = PROV_ICONS[prov.key] || Pencil;

  const amountText = `${
    txn.type === 'income'     ? '+' :
    txn.type === 'investment' ? '\u2191' :
    txn.type === 'transfer'   ? '\u21c4 ' :
    txn.type === 'refund'     ? '\u21ba ' :
    '\u2212'
  }${formatMoney(Math.abs(Number(txn.amount)), currency, { compact: true })}`;

  const amountClass =
    txn.type === 'income'    ? 'text-[hsl(var(--chart-5))]' :
    txn.type === 'transfer'  ? 'text-muted-foreground'      :
    txn.type === 'refund'    ? 'text-muted-foreground'      :
    'text-foreground';

  return (
    <div
      className={`group grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
        selected
          ? 'border-primary/40 bg-accent/60'
          : 'border-border/70 bg-card hover:bg-accent/30'
      }`}
      data-testid="transaction-row"
      data-selected={selected ? 'true' : 'false'}
    >
      {/* Bulk-select checkbox. Kept always visible so the affordance is
          discoverable (matches Gmail / Notion / Linear pattern). Phase
          84.19.2 — bumped idle opacity 60 → 85% because beta users were
          missing the affordance on production statement-heavy pages. */}
      <div
        className={`shrink-0 transition-opacity ${
          selectionMode || selected
            ? 'opacity-100'
            : 'opacity-85 group-hover:opacity-100 focus-within:opacity-100'
        }`}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect?.(txn.id)}
          aria-label={`Select transaction ${titleText}`}
          data-testid={`transaction-select-${txn.id}`}
        />
      </div>

      {/* Avatar-style account badge. Deterministic tint per account. */}
      <button
        type="button"
        onClick={() => onOpen?.(txn)}
        className="h-9 w-9 rounded-xl grid place-items-center text-[11px] font-semibold shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        style={{ background: tint.bg, color: tint.fg }}
        data-testid={`transaction-account-badge-${txn.id}`}
        aria-label={accountName || 'Account'}
        title={accountName || 'Account'}
      >
        {initials}
      </button>

      {/* Main cluster — description + chips row. Clicking anywhere here
          opens the drawer. */}
      <button
        type="button"
        onClick={() => onOpen?.(txn)}
        className="min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg py-0.5 pr-1"
        data-testid={`transaction-row-${txn.id}`}
        aria-label={`Open transaction ${titleText}`}
      >
        <div className="text-sm font-medium truncate flex items-center gap-1.5" title={pretty.raw || titleText}>
          <span className="truncate">{titleText}</span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-muted-foreground shrink-0 opacity-60 hover:opacity-100"
                  data-testid={`transaction-provenance-${txn.id}`}
                  aria-label={prov.label}
                >
                  <ProvIcon className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">{prov.label}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
          {pretty.mode && (
            <Badge variant="outline" className="rounded-full text-[10px] font-medium uppercase tracking-wide px-1.5 py-0">
              {modeLabel(pretty.mode)}
            </Badge>
          )}
          <Badge
            variant={conf.key === 'low' ? 'outline' : 'secondary'}
            className={`rounded-full text-[10px] px-1.5 py-0 ${
              conf.key === 'low'
                ? 'border-amber-500/40 text-amber-700 dark:text-amber-400 bg-transparent'
                : ''
            }`}
            data-testid={`transaction-category-chip-${txn.id}`}
          >
            {txn.category || 'Miscellaneous'}
            {conf.key === 'low' && <AlertTriangle className="h-2.5 w-2.5 ml-1 inline" />}
          </Badge>
          {txn.recurring && (
            <Badge variant="outline" className="rounded-full text-[10px] px-1.5 py-0">recurring</Badge>
          )}
          {/* Phase 84.20.5 — "matched by note" chip. Only shown when the
              current search matched this row exclusively via `txn.note`.
              Amber tint keeps it visually distinct from the neutral
              category/mode/recurring chips. */}
          {matchedByNote && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="rounded-full text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/5 gap-1"
                    data-testid={`transaction-note-match-${txn.id}`}
                  >
                    <StickyNote className="h-2.5 w-2.5" />
                    note
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[260px]">
                  Matched your search in this transaction&apos;s note.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {accountName && (
            <span className="truncate opacity-80">· {accountName}</span>
          )}
        </div>
      </button>

      {/* Amount cluster */}
      <div className={`text-sm font-semibold tabular-nums shrink-0 ${amountClass}`} data-testid={`transaction-amount-${txn.id}`}>
        {amountText}
      </div>
    </div>
  );
}
